import React, { useState, useEffect, useRef } from 'react'
import CabinetFooter from './CabinetFooter'
import { useIsMobile } from '../../hooks/useIsMobile'

const BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:8000'

/* ─── IBKR connection indicator (🟢/🟡/🔴) — polls /api/connection/status ─── */
const CONN_UI = {
  connected:    { color: '#10B981', label: 'IBKR',   title: 'IBKR: підключено' },
  reconnecting: { color: '#F59E0B', label: 'IBKR',   title: 'IBKR: перепідключення…' },
  down:         { color: '#EF4444', label: 'IBKR',   title: 'IBKR: немає звʼязку' },
  unknown:      { color: 'rgba(255,255,255,0.35)', label: 'IBKR', title: 'IBKR: статус невідомий' },
}

export function ConnectionIndicator({ light = false } = {}) {
  const [info, setInfo] = useState(null)   // {state, last_event, occurred_at} | null

  useEffect(() => {
    let alive = true
    const poll = () => {
      const token = localStorage.getItem('token')
      if (!token) return
      fetch(`${BASE}/api/connection/status`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (alive) setInfo(d) })
        .catch(() => { if (alive) setInfo(null) })
    }
    poll()
    const id = setInterval(poll, 15000)
    return () => { alive = false; clearInterval(id) }
  }, [])

  const state = info?.state && CONN_UI[info.state] ? info.state : 'unknown'
  const ui = CONN_UI[state]
  const title = info?.last_event
    ? `${ui.title} (остання подія: ${info.last_event}${info.occurred_at ? ' · ' + new Date(info.occurred_at).toLocaleString('uk-UA') : ''})`
    : ui.title

  return (
    <div
      title={title}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '0 10px', height: 32,
        background: light ? '#F3F4F6' : 'rgba(255,255,255,0.06)',
        border: light ? '1px solid #E5E7EB' : '1px solid rgba(255,255,255,0.1)',
        borderRadius: 6, flexShrink: 0,
      }}
    >
      <span style={{
        width: 8, height: 8, borderRadius: '50%', background: ui.color, flexShrink: 0,
        boxShadow: state === 'connected' ? `0 0 6px ${ui.color}` : 'none',
      }} />
      <span style={{ fontSize: 12, color: light ? '#475467' : 'rgba(255,255,255,0.78)', fontFamily: "'Inter',sans-serif", whiteSpace: 'nowrap' }}>
        {ui.label}
      </span>
    </div>
  )
}

/* ─── inline SVG icons (same shapes as original App.js) ─────────── */
function AvatarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="7" r="4" fill="rgba(100,116,139,0.9)" />
      <path d="M3 18c0-3.866 3.134-7 7-7s7 3.134 7 7" fill="rgba(100,116,139,0.9)" />
    </svg>
  )
}

function ChevronIcon({ open }) {
  return (
    <svg
      width="11" height="11" viewBox="0 0 12 12" fill="none"
      style={{ transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
    >
      <path d="M2 4l4 4 4-4" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function LogoutIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="rgba(255,255,255,0.85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  )
}

/* ─── user photo in the header ───────────────────────────────────────
   Фото з кабінету («Мої дані») показуємо в кружечку шапки. Джерело — той самий
   /api/user/profile; кеш у localStorage, щоб фото не блимало при переходах,
   а кабінет після зміни фото шле подію 'avatar-changed' (ProfileCards). */
const AVATAR_KEY = 'avatar'
function readCachedAvatar() {
  try { return localStorage.getItem(AVATAR_KEY) || null } catch { return null }
}
export function publishAvatar(avatar) {
  try {
    if (avatar) localStorage.setItem(AVATAR_KEY, avatar)
    else localStorage.removeItem(AVATAR_KEY)
  } catch { /* storage may be unavailable */ }
  window.dispatchEvent(new Event('avatar-changed'))
}
function useUserAvatar() {
  const [avatar, setAvatar] = useState(readCachedAvatar)
  useEffect(() => {
    const onChange = () => setAvatar(readCachedAvatar())
    window.addEventListener('avatar-changed', onChange)
    const token = localStorage.getItem('token')
    if (token) {
      fetch(`${BASE}/api/user/profile`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (d && d.user) publishAvatar(d.user.avatar || null) })
        .catch(() => {})
    }
    return () => window.removeEventListener('avatar-changed', onChange)
  }, [])
  return avatar
}

function AvatarCircle() {
  const avatar = useUserAvatar()
  return (
    <div style={{
      width: 28, height: 28, borderRadius: '50%', overflow: 'hidden',
      background: 'rgba(229,231,235,1)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      {avatar
        ? <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : <AvatarIcon />}
    </div>
  )
}

/* ─── avatar as a plain link to the cabinet (no dropdown) ─────────── */
function AvatarLink({ username, active, onClick, compact = false, title = 'Мій кабінет' }) {
  return (
    <button
      onClick={onClick}
      title={compact ? `${title} — ${username}` : title}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '4px 8px', background: active ? 'rgba(255,255,255,0.10)' : 'transparent',
        border: 'none', borderRadius: 8, cursor: 'pointer', color: '#fff',
        transition: 'background 0.15s', flexShrink: 0,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)' }}
      onMouseLeave={e => { e.currentTarget.style.background = active ? 'rgba(255,255,255,0.10)' : 'transparent' }}
    >
      <AvatarCircle />
      {/* On phones the username (often a long email) is dropped to keep the header
          on one row; the avatar itself still links to the cabinet. */}
      {!compact && (
        <span style={{
          fontSize: 13, fontFamily: "'Inter',sans-serif", whiteSpace: 'nowrap',
          maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {username}
        </span>
      )}
    </button>
  )
}

/* ─── logout icon button ─────────────────────────────────────────── */
function LogoutButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      title="Вийти"
      aria-label="Вийти"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 36, height: 36, background: 'transparent',
        border: 'none', borderRadius: 8, cursor: 'pointer', flexShrink: 0,
        transition: 'background 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
    >
      <LogoutIcon />
    </button>
  )
}

/* ─── hamburger / close icon (mobile menu trigger) ───────────────── */
function HamburgerIcon({ open }) {
  const bar = {
    position: 'absolute', left: 4, right: 4, height: 2, borderRadius: 2,
    background: '#fff', transition: 'transform 0.2s, opacity 0.2s, top 0.2s',
  }
  return (
    <span style={{ position: 'relative', display: 'inline-block', width: 22, height: 16 }}>
      <span style={{ ...bar, top: open ? 7 : 1, transform: open ? 'rotate(45deg)' : 'none' }} />
      <span style={{ ...bar, top: 7, opacity: open ? 0 : 1 }} />
      <span style={{ ...bar, top: open ? 7 : 13, transform: open ? 'rotate(-45deg)' : 'none' }} />
    </span>
  )
}

/* ─── mobile menu item (row in the hamburger dropdown) ───────────── */
function MobileMenuItem({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'block', width: '100%', padding: '11px 16px',
        background: active ? '#F1F5FF' : 'transparent', border: 'none', cursor: 'pointer',
        textAlign: 'left', fontSize: 15,
        color: active ? '#163381' : '#101828',
        fontWeight: active ? 600 : 400,
        fontFamily: "'Inter',sans-serif",
        transition: 'background 0.12s',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#F9FAFB' }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
    >
      {label}
    </button>
  )
}

/* ─── single nav button ──────────────────────────────────────────── */
function NavButton({ label, active, onClick }) {
  const [hovered, setHovered] = useState(false)
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '0 13px',
        background: 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid #6385FF' : '2px solid transparent',
        color: active ? '#fff' : (hovered ? '#fff' : 'rgba(255,255,255,0.62)'),
        fontSize: 13,
        fontFamily: "'Inter','Segoe UI',sans-serif",
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'color 0.15s, border-color 0.15s',
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {label}
    </button>
  )
}

/* ─── profile dropdown ───────────────────────────────────────────── */
function ProfileDropdown({ username, navigateTo, onLogout, activeNav }) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  useEffect(() => {
    if (!open) return
    function onOutsideClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onOutsideClick)
    return () => document.removeEventListener('mousedown', onOutsideClick)
  }, [open])

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      {/* trigger */}
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '4px 8px', background: 'transparent', border: 'none',
          borderRadius: 6, cursor: 'pointer', color: '#fff',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
      >
        <AvatarCircle />
        <span style={{
          fontSize: 13, fontFamily: "'Inter',sans-serif", whiteSpace: 'nowrap',
          maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {username}
        </span>
        <ChevronIcon open={open} />
      </button>

      {/* dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 6px)',
          minWidth: 180, background: '#fff',
          border: '1px solid #E5E7EB', borderRadius: 12,
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
          padding: '4px 0', zIndex: 300,
        }}>
          <button
            onClick={() => { navigateTo('/settings'); setOpen(false) }}
            style={{
              display: 'block', width: '100%', padding: '8px 16px',
              background: 'transparent', border: 'none', cursor: 'pointer',
              textAlign: 'left', fontSize: 14,
              color: activeNav === 'my-account' ? '#163381' : '#101828',
              fontWeight: activeNav === 'my-account' ? 600 : 400,
              fontFamily: "'Inter',sans-serif",
              transition: 'background 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F9FAFB' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
          >
            👤 Мій кабінет
          </button>

          <div style={{ height: 1, background: '#E5E7EB', margin: '2px 0' }} />

          <button
            onClick={() => { onLogout(); setOpen(false) }}
            style={{
              display: 'block', width: '100%', padding: '8px 16px',
              background: 'transparent', border: 'none', cursor: 'pointer',
              textAlign: 'left', fontSize: 14, color: '#101828',
              fontFamily: "'Inter',sans-serif",
              transition: 'background 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F9FAFB' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
          >
            Вийти
          </button>
        </div>
      )}
    </div>
  )
}

/* ─── routes that render edge-to-edge (no shell padding) ─────────── */
const NO_PADDING = new Set([
  'analyzer', 'data', 'market', 'single', 'running', 'trades-ticker',
])

/* ─── AppShell ───────────────────────────────────────────────────── */
export default function AppShell({
  children,
  component,
  navigateTo,
  username,
  userRole,
  onLogout,
  activeNav,
  navItems,
  variant = 'calc',
}) {
  const isTrading = variant === 'trading';
  const isMobile  = useIsMobile();
  // Телефон горизонтально (~700-1000 px): меню вже не гамбургер, але ширини
  // замало — гасло біля логотипа ховаємо, щоб імʼя в шапці не обрізалось (29.10).
  const isNarrow  = useIsMobile(1180);
  const headerBg  = isTrading ? '#243D81' : '#163381';
  const bgPage    = isTrading ? '#F9FAFB' : '#F4F6FB';

  // ── Mobile hamburger menu (trading variant) ──
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onEsc  = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onEsc); };
  }, [menuOpen]);
  // Close the menu automatically once we've dropped back to desktop width.
  useEffect(() => { if (!isMobile) setMenuOpen(false); }, [isMobile]);
  const menuGo = (path) => { navigateTo(path); setMenuOpen(false); };

  return (
    <div style={{ minHeight: '100vh', background: bgPage, color: '#101828', fontFamily: "'Inter','Segoe UI',system-ui,sans-serif" }}>

      {/* ── Header ──────────────────────────────────────────────── */}
      <header style={{
        background: headerBg,
        position: 'sticky',
        top: 0,
        zIndex: 200,
        boxShadow: '0 1px 0 rgba(0,0,0,0.25)',
        height: 56,
        display: 'flex',
        alignItems: 'center',
        padding: isMobile ? '0 10px' : '0 16px',
        gap: 8,
        boxSizing: 'border-box',
      }}>

        {/* Logo — left. Однаковий на обох сайтах (менеджер 03.11): назва +
            підпис праворуч від риски; на вузькому екрані підпис ховається.
            Trading веде на публічний сайт, бектестинг — на головну, тобто
            аналізатор (App.js canonicalPath переписує '/' на '/analyzer'). */}
        {(() => {
          const logo = (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 18, letterSpacing: '-0.3px' }}>TRADING</span>
                <span style={{ color: 'rgb(177,196,247)', fontWeight: 700, fontSize: 12 }}>.com.ua</span>
              </div>
              {!isNarrow && (
                <div style={{
                  borderLeft: '1px solid rgba(255,255,255,0.25)', paddingLeft: 16,
                  color: 'rgba(252,252,252,0.85)', fontSize: 12, lineHeight: 1.3, fontWeight: 500,
                  maxWidth: 220,
                }}>
                  №1 автоматична торгівля<br/>акціями по алгоритмам
                </div>
              )}
            </>
          );
          const boxStyle = { flexShrink: 0, display: 'flex', alignItems: 'center', gap: 16, textDecoration: 'none' };
          return isTrading ? (
            <a
              href={`${window.location.protocol}//${window.location.host.replace(/^app\./, '')}/`}
              style={boxStyle}
              title="На головну trading.com.ua"
            >
              {logo}
            </a>
          ) : (
            <div onClick={() => navigateTo('/')} title="На головну" style={{ ...boxStyle, cursor: 'pointer' }}>
              {logo}
            </div>
          );
        })()}

        {isMobile ? (
            /* ── Mobile (обидва варіанти): гамбургер із випадним меню. У
               бектестингу раніше мобільної версії не було зовсім — меню, три
               адмінські кнопки й профіль стояли в один рядок і вилазили за
               екран (30.09). ── */
            <>
              <div style={{ flex: 1, minWidth: 8 }} />
              <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }}>
                <button
                  onClick={() => setMenuOpen(v => !v)}
                  aria-label="Меню"
                  aria-expanded={menuOpen}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 40, height: 40, background: menuOpen ? 'rgba(255,255,255,0.12)' : 'transparent',
                    border: 'none', borderRadius: 8, cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  <HamburgerIcon open={menuOpen} />
                </button>

                {menuOpen && (
                  <div style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                    minWidth: 220, background: '#fff',
                    border: '1px solid #E5E7EB', borderRadius: 12,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
                    padding: '6px 0', zIndex: 400,
                  }}>
                    {/* Шапка меню: іконка + імʼя крупніше й жирніше (менеджер 13.10). */}
                    {/* У кабінеті бота шапка меню — посилання на головну (менеджер 29.10). */}
                    <div
                      role={isTrading ? 'button' : undefined}
                      onClick={isTrading ? () => menuGo('/') : undefined}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 16px 8px', fontFamily: "'Inter',sans-serif", minWidth: 0,
                        cursor: isTrading ? 'pointer' : 'default',
                      }}>
                      <AvatarCircle />
                      <span style={{
                        fontSize: 17, fontWeight: 700, color: '#101828',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {username}
                      </span>
                    </div>
                    <div style={{ height: 1, background: '#E5E7EB', margin: '2px 0 4px' }} />

                    {/* Кабінет бота: Мій акаунт → Адміністрування (лише адмін;
                        звичайний користувач цього пункту не бачить) → Налаштування. */}
                    {navItems.map(item => (
                      <React.Fragment key={item.id}>
                        {isTrading && item.id === 'my-account' && userRole === 'admin' && (
                          <MobileMenuItem
                            label="Адміністрування"
                            active={activeNav === 'admin' || activeNav === 'connection-log'}
                            onClick={() => menuGo('/admin')}
                          />
                        )}
                        <MobileMenuItem
                          label={item.label}
                          active={activeNav === item.id}
                          onClick={() => menuGo(item.path)}
                        />
                      </React.Fragment>
                    ))}

                    {userRole === 'admin' && !isTrading && (
                      <>
                        <MobileMenuItem label="⚙️ Адміністрування" active={activeNav === 'admin'}
                                        onClick={() => menuGo('/admin')} />
                        <MobileMenuItem label="🖥 Кластер" active={activeNav === 'workers'}
                                        onClick={() => menuGo('/cluster')} />
                        <MobileMenuItem label="🔌 Лог" active={activeNav === 'connection-log'}
                                        onClick={() => menuGo('/connection-log')} />
                      </>
                    )}

                    {!isTrading && (
                      <MobileMenuItem
                        label="👤 Мій кабінет"
                        active={activeNav === 'my-account'}
                        onClick={() => menuGo('/settings')}
                      />
                    )}

                    <div style={{ height: 1, background: '#E5E7EB', margin: '4px 0' }} />

                    <MobileMenuItem label="Вийти" onClick={() => { setMenuOpen(false); onLogout(); }} />
                  </div>
                )}
              </div>
            </>
          ) : isTrading ? (
          /* ── Trading desktop: усі пункти меню справа + аватар-лінк + окрема іконка виходу ── */
          <>
            <div style={{ flex: 1, minWidth: 8 }} />
            <nav style={{
              flexShrink: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              gap: 4,
              overflowX: 'auto',
            }}>
              {/* Порядок (менеджер 13.10): Мій акаунт → Адміністрування (лише
                  адмін) → Налаштування. */}
              {navItems.map(item => (
                <React.Fragment key={item.id}>
                  {item.id === 'my-account' && userRole === 'admin' && (
                    <NavButton
                      label="Адміністрування"
                      active={activeNav === 'admin' || activeNav === 'connection-log'}
                      onClick={() => navigateTo('/admin')}
                    />
                  )}
                  <NavButton
                    label={item.label}
                    active={activeNav === item.id}
                    onClick={() => navigateTo(item.path)}
                  />
                </React.Fragment>
              ))}

            </nav>

            {/* Аватар і вихід — ПОЗА прокручуваним меню: усередині нього на
                вузькому екрані (телефон горизонтально) обрізалось саме імʼя. */}
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 1, height: 26, background: 'rgba(255,255,255,0.12)', flexShrink: 0, margin: '0 6px' }} />

              {/* Аватар веде на головну кабінету (менеджер 29.10). */}
              <AvatarLink
                username={username}
                active={activeNav === 'dashboard'}
                onClick={() => navigateTo('/')}
                title="Мій акаунт"
              />

              <LogoutButton onClick={onLogout} />
            </div>
          </>
        ) : (
          /* ── Calc: центроване меню + IBKR-індикатор + admin/log pills + дропдаун ── */
          <>
            <nav style={{
              flex: 1,
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'center',
              height: '100%',
              overflowX: 'auto',
              gap: 0,
              minWidth: 0,
            }}>
              {navItems.map(item => (
                <NavButton
                  key={item.id}
                  label={item.label}
                  active={activeNav === item.id}
                  onClick={() => navigateTo(item.path)}
                />
              ))}
            </nav>

            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>

              {/* Індикатор звʼязку з IBKR — лише в кабінеті бота. У бектестингу
                  брокера немає взагалі, тож лампочка там завжди сіра («статус
                  невідомий») і при цьому раз на 15 секунд смикає
                  /api/connection/status. Прибрано разом із бот-пунктами меню
                  (26.08). */}
              {isTrading && <ConnectionIndicator />}

              {userRole === 'admin' && (
                <button
                  onClick={() => navigateTo('/admin')}
                  style={{
                    padding: '0 12px',
                    height: 32,
                    background: activeNav === 'admin' ? 'rgba(99,133,255,0.18)' : 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 6,
                    color: activeNav === 'admin' ? '#fff' : 'rgba(255,255,255,0.75)',
                    fontSize: 12.5,
                    fontFamily: "'Inter',sans-serif",
                    fontWeight: activeNav === 'admin' ? 600 : 400,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => {
                    if (activeNav !== 'admin') {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.12)'
                      e.currentTarget.style.color = '#fff'
                    }
                  }}
                  onMouseLeave={e => {
                    if (activeNav !== 'admin') {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.06)'
                      e.currentTarget.style.color = 'rgba(255,255,255,0.75)'
                    }
                  }}
                >
                  ⚙️ Адміністрування
                </button>
              )}

              {/* Машини парку — лише адмінам і лише в бектестингу: у боті
                  перебору немає, тож і показувати нема чого. */}
              {userRole === 'admin' && !isTrading && (
                <button
                  onClick={() => navigateTo('/cluster')}
                  title="Машини, що рахують перебір"
                  style={{
                    padding: '0 12px', height: 32,
                    background: activeNav === 'workers' ? 'rgba(99,133,255,0.18)' : 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
                    color: activeNav === 'workers' ? '#fff' : 'rgba(255,255,255,0.75)',
                    fontSize: 12.5, fontFamily: "'Inter',sans-serif",
                    fontWeight: activeNav === 'workers' ? 600 : 400,
                    cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                  }}
                >
                  🖥 Кластер
                </button>
              )}

              {userRole === 'admin' && (
                <button
                  onClick={() => navigateTo('/connection-log')}
                  title="IBKR Connection Log"
                  style={{
                    padding: '0 12px', height: 32,
                    background: activeNav === 'connection-log' ? 'rgba(99,133,255,0.18)' : 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
                    color: activeNav === 'connection-log' ? '#fff' : 'rgba(255,255,255,0.75)',
                    fontSize: 12.5, fontFamily: "'Inter',sans-serif",
                    fontWeight: activeNav === 'connection-log' ? 600 : 400,
                    cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                  }}
                >
                  🔌 Лог
                </button>
              )}

              <div style={{ width: 1, height: 26, background: 'rgba(255,255,255,0.12)', flexShrink: 0 }} />

              <ProfileDropdown
                username={username}
                navigateTo={navigateTo}
                onLogout={onLogout}
                activeNav={activeNav}
              />
            </div>
          </>
        )}
      </header>

      {/* ── Main content ────────────────────────────────────────── */}
      <main style={{
        minHeight: 'calc(100vh - 56px)',
        padding: NO_PADDING.has(component) ? 0 : (isMobile ? '10px 12px 16px' : '24px 28px'),
        // Clip stray horizontal overflow so the whole page never scrolls sideways;
        // wide tables keep their own inner overflow-x scroll containers.
        overflowX: 'hidden',
        overflowY: 'auto',
      }}>
        {children}
      </main>

      {/* ── Footer (trading member area only) ───────────────────── */}
      {isTrading && <CabinetFooter />}
    </div>
  )
}
